import React, { useState } from "react";
import {
  TableWrapper,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
} from "./TableComponent.styles";
import { theme } from "@src/theme";
import SortIcons from "./SortIcons";
import Tooltip from "../Tooltip";
import Spinner from "../Spinner";
interface Column {
  Header: string;
  accessor: string;
  Cell?: (props: { row: any; value: any }) => React.ReactNode;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  enableSort?: boolean;
  centerContent?: boolean;
  textOverflow?: string;
  hidden?: boolean;
}

interface TableProps {
  onRowClick?: (row: any) => void;
  columns: Column[];
  data: any[];
  searchTerm?: string;
  $compactUI?: boolean;
  $expandedHeight?: string;
  onSortCb?: ({
    accessor,
    sort,
  }: {
    accessor: string;
    sort: "" | "asc" | "desc";
  }) => void;
  loaderRef?: React.RefObject<HTMLTableRowElement>;
  variant?: "darkBg" | "grayBg" | "inherit";
  loadingNextPage?: boolean;
}

const TableComponent: React.FC<TableProps> = ({
  loaderRef,
  onSortCb,
  $expandedHeight,
  $compactUI,
  columns,
  data,
  onRowClick,
  variant = "darkBg",
  loadingNextPage,
}) => {
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"" | "asc" | "desc">("asc");

  const handleSort = (column: Column): void => {
    if (!column.enableSort) return;
    const accessor = column.accessor;
    let newSortOrder: "" | "asc" | "desc" = "asc";
    if (sortColumn === accessor) {
      if (sortOrder === "asc") {
        newSortOrder = "desc";
      } else if (sortOrder === "desc") {
        newSortOrder = "";
      }
    }
    setSortColumn(newSortOrder ? accessor : "");
    setSortOrder(newSortOrder);
    onSortCb?.({ accessor, sort: newSortOrder });
  };

  return (
    <>
      <TableWrapper variant={variant}>
        <Table>
          <TableHeader variant={variant}>
            <TableRow variant={variant}>
              {columns.map(
                (column, colIndex) =>
                  !column.hidden && (
                    <TableHeaderCell
                      $centerValues={column.centerContent}
                      $index={colIndex}
                      key={column.accessor + column.Header}
                      $compactUI={$compactUI}
                      onClick={() => handleSort(column)}
                      style={{
                        width: column.width || "auto",
                        minWidth: column.minWidth || "auto",
                        maxWidth: column.maxWidth || "auto",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {column.Header}
                        </div>
                        {column.enableSort && (
                          <SortIcons
                            sortColumn={sortColumn}
                            columnAccessor={column.accessor}
                            sortOrder={sortOrder}
                          />
                        )}
                      </div>
                    </TableHeaderCell>
                  )
              )}
            </TableRow>
          </TableHeader>
          <tbody style={{ zIndex: 1 }}>
            {data.map((row, index) => (
              <TableRow
                variant={variant}
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRowClick?.(row);
                }}
                style={{
                  zIndex: 1,
                  cursor: onRowClick ? "pointer" : "default",
                }}
                key={row.id}
              >
                {columns.map(
                  (column, colIndex) =>
                    !column.hidden && (
                      <TableCell
                        variant={variant}
                        $centerValues={column.centerContent}
                        $expandedHeight={$expandedHeight}
                        $index={colIndex}
                        $textOverflow={column.textOverflow}
                        $compactUI={$compactUI}
                        key={column.accessor + column.Header}
                        style={{
                          width: column.width || "auto",
                          minWidth: column.minWidth || "auto",
                          maxWidth: column.maxWidth || "auto",
                        }}
                      >
                        {column.accessor === "display_row_index" ? (
                          <span
                            style={{
                              borderBottom:
                                column.accessor === "display_row_index"
                                  ? `2px solid ${theme.colors.yellow}`
                                  : "none",
                            }}
                          >
                            {index + 1}
                          </span>
                        ) : column.Cell ? (
                          column.Cell({
                            value: row[column.accessor],
                            row,
                          })
                        ) : (
                          <Tooltip content={row[column.accessor]}>
                            {row[column.accessor]}
                          </Tooltip>
                        )}
                      </TableCell>
                    )
                )}
              </TableRow>
            ))}
            {loaderRef && (
              <TableRow
                ref={loaderRef}
                style={{ height: 1, width: "100%" }}
              ></TableRow>
            )}
            {loadingNextPage && (
              <TableRow
                variant={variant}
                style={{
                  height: 60,
                  width: "100%",
                  backgroundColor: "transparent",
                }}
              >
                <td
                  colSpan={columns.filter((c) => !c.hidden).length}
                  style={{ borderBottom: "none", padding: "20px 0" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Spinner small />
                  </div>
                </td>
              </TableRow>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </>
  );
};

export default TableComponent;
